<script setup>
// Single mount point for the branded confirm dialog. Driven by useFangateConfirm.
// Teleported to <body> so it layers above every other modal; :root CSS vars still
// apply because they cascade through <body>.
import { onMounted, onUnmounted } from 'vue';
import { useFangateConfirmHost } from 'dashboard/composables/useFangateConfirm';

const { state, settle } = useFangateConfirmHost();

// Keyboard: Esc cancels, Enter confirms — only while open.
const onKey = e => {
  if (!state.open) return;
  if (e.key === 'Escape') { e.preventDefault(); settle(false); }
  else if (e.key === 'Enter') { e.preventDefault(); settle(true); }
};
onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <Teleport to="body">
    <transition name="fc-fade">
      <div v-if="state.open" class="fc-overlay" @click.self="settle(false)">
        <div class="fc-modal" role="alertdialog" aria-modal="true">
          <h3 class="fc-title">{{ state.title }}</h3>
          <p v-if="state.message" class="fc-message">{{ state.message }}</p>
          <div class="fc-actions">
            <button class="fc-btn fc-btn--ghost" @click="settle(false)">
              {{ state.cancelLabel }}
            </button>
            <button
              class="fc-btn"
              :class="state.danger ? 'fc-btn--danger' : 'fc-btn--primary'"
              autofocus
              @click="settle(true)"
            >
              {{ state.confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.fc-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000; /* above every other Fangate modal (those top out at ~10000) */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(18, 22, 22, 0.46);
  backdrop-filter: blur(2px);
}
.fc-modal {
  width: 100%;
  max-width: 420px;
  background: var(--bg-panel, #fff);
  border: 1px solid var(--border-soft, #e0d8c5);
  border-radius: 14px;
  padding: 22px 22px 18px;
  box-shadow: 0 18px 48px rgba(31, 42, 42, 0.22);
}
.fc-title {
  margin: 0 0 8px;
  font-family: 'Inter', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: var(--ink-strong, #0b1414);
}
.fc-message {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ink-medium, #2a3434);
  white-space: pre-line; /* honour \n\n in multi-paragraph messages */
}
.fc-actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  margin-top: 20px;
}
.fc-btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.fc-btn--ghost {
  background: var(--bg-panel, #fff);
  border-color: var(--border-soft, #e0d8c5);
  color: var(--ink-medium, #2a3434);
}
.fc-btn--ghost:hover {
  background: var(--bg-subtle, #f1ede4);
  color: var(--ink-strong, #0b1414);
}
.fc-btn--primary {
  background: var(--accent, #0d9488);
  color: #fff;
}
.fc-btn--primary:hover {
  background: var(--accent-strong, #0f766e);
}
.fc-btn--danger {
  background: #b91c1c;
  color: #fff;
}
.fc-btn--danger:hover {
  background: #991b1b;
}

.fc-fade-enter-active,
.fc-fade-leave-active { transition: opacity 0.14s ease; }
.fc-fade-enter-from,
.fc-fade-leave-to { opacity: 0; }
</style>
